import React, { useState, useEffect } from 'react';
import api from '../../../../config/axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { FaTimes, FaRegSave } from 'react-icons/fa';

const EstornosItemModal = ({ item, onClose, onEstornoRealizado, readOnly = false }) => {
    const [estornos, setEstornos] = useState([]);
    const [formData, setFormData] = useState({
        quantidade_estorno: 1,
        tipo_pagamento: '',
        motivo: ''
    });

    useEffect(() => {
        if (item?.id) {
            fetchEstornos();
        }
    }, [item]);

    const fetchEstornos = async () => {
        try {
            Swal.fire({
                title: "Carregando...",
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await api.get(`/comandas/itens/${item.id}/estornos`);
            
            if (response.data.success) {
                setEstornos(response.data.data.estornos || []);
            } else {
                Swal.fire('Erro', 'Erro ao carregar estornos do item.', 'error');
            }
            Swal.close();
        } catch (error) {
            Swal.close();
            Swal.fire('Erro', 'Erro ao carregar estornos do item.', 'error');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const realizarEstorno = async (e) => {
        e.preventDefault();

        if (!formData.quantidade_estorno || formData.quantidade_estorno <= 0) {
            Swal.fire('Atenção', 'Por favor, informe uma quantidade válida para o estorno.', 'warning');
            return;
        }

        const quantidadeDisponivel = item.qtd - calcularTotalEstornado();
        
        if (formData.quantidade_estorno > quantidadeDisponivel) {
            Swal.fire('Atenção', `A quantidade do estorno não pode ser maior que a quantidade disponível (${quantidadeDisponivel}).`, 'warning');
            return;
        }

        if (!formData.tipo_pagamento) {
            Swal.fire('Atenção', 'Por favor, selecione um método de pagamento.', 'warning');
            return;
        }

        if (!formData.motivo || !formData.motivo.trim()) {
            Swal.fire('Atenção', 'Por favor, informe o motivo do estorno.', 'warning');
            return;
        }

        const result = await Swal.fire({
            title: 'Confirmar Estorno',
            text: `Deseja estornar ${formData.quantidade_estorno} unidade(s) do item "${getItemName()}"? (A comanda ficará aberta)`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, estornar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: "Carregando...",
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const response = await api.post(`/comandas/itens/${item.id}/estornar`, {
                    quantidade_estorno: parseInt(formData.quantidade_estorno),
                    tipo_pagamento: formData.tipo_pagamento,
                    motivo: formData.motivo
                });

                if (response.data.success) {
                    Swal.close();
                    
                    await Swal.fire(
                        'Sucesso!', 
                        `O item "${getItemName()}" na quantidade de ${formData.quantidade_estorno} foi estornado com sucesso.`, 
                        'success'
                    );
                    
                    setFormData({
                        quantidade_estorno: 1,
                        tipo_pagamento: '',
                        motivo: ''
                    });
                    
                    fetchEstornos();
                    
                    if (onEstornoRealizado) {
                        const novoTotalEstornado = calcularTotalEstornado() + parseInt(formData.quantidade_estorno);
                        const itemCompletamenteEstornado = novoTotalEstornado >= item.qtd;
                        
                        onEstornoRealizado({
                            itemEstornado: item,
                            itemCompletamenteEstornado
                        });
                        onClose();
                    }
                } else {
                    Swal.fire('Erro', response.data.message || 'Erro ao realizar o estorno.', 'error');
                }
            } catch (error) {
                Swal.close();
                Swal.fire('Erro', error.response.data.message || 'Erro ao realizar o estorno.', 'error');
            }
        }
    };

    const getItemName = () => {
        if (item.tipo_item === 'servico') {
            return item.servico?.label || 'Serviço';
        } else {
            return item.produto?.label || 'Produto';
        }
    };

    const formatarMoeda = (valor) => {
        if (!valor) return 'R$ 0,00';
        return parseFloat(valor).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    const formatarDataHora = (data) => {
        return moment(data).format('DD/MM/YYYY HH:mm:ss');
    };

    const calcularTotalEstornado = () => {
        return estornos.reduce((total, estorno) => {
            return total + (estorno.quantidade_estornada || 0);
        }, 0);
    };

    const isItemCompletamenteEstornado = () => {
        return calcularTotalEstornado() >= item.qtd;
    };

    return (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h4 className="fw-bold mt-2">
                            {readOnly ? 'Visualizar Estornos do Item' : 'Estornos do Item'}
                        </h4>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body">
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="row">
                                    <div className="col-md-6 col-12">
                                        <strong>Item:</strong> {getItemName()}
                                    </div>
                                    <div className="col-md-3 col-12 mt-md-0 mt-3">
                                        <strong>Quantidade Total:</strong> {item.qtd}
                                    </div>
                                    <div className="col-md-3 col-12 mt-md-0 mt-3">
                                        <strong>Já Estornado:</strong> 
                                        <span className={calcularTotalEstornado() > 0 ? "ms-1" : "ms-1"}>
                                            {calcularTotalEstornado()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-4">
                            <div className="col-12">
                                <h6 className="mb-3">Histórico</h6>
                                {estornos.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-striped">
                                            <thead className="table-custom-primary">
                                                <tr>
                                                    <th>Data e Hora</th>
                                                    <th>Quantidade</th>
                                                    <th>Valor Estornado</th>
                                                    <th>Motivo</th>
                                                    <th>Responsável</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {estornos.map((estorno) => (
                                                    <tr key={estorno.id}>
                                                        <td className="text-nowrap">
                                                            {formatarDataHora(estorno.data_estorno)}
                                                        </td>
                                                        <td className="text-center">
                                                            {estorno.quantidade_estornada}
                                                        </td>
                                                        <td>
                                                            {formatarMoeda(estorno.valor_total_estornado)}
                                                        </td>
                                                        <td>
                                                            {estorno.motivo}
                                                        </td>
                                                        <td>
                                                            {estorno.criado_por || 'N/A'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="alert alert-info">
                                        <i className="fas fa-info-circle me-2"></i>
                                        Nenhum estorno encontrado para este item.
                                    </div>
                                )}
                            </div>
                        </div>

                        {!readOnly && (
                            <div className="row">
                                <div className="col-12">
                                    <h6 className="mb-3">Realizar Novo Estorno</h6>
                                    
                                    {isItemCompletamenteEstornado() ? (
                                        <div className="alert alert-warning">
                                            <i className="fas fa-exclamation-triangle me-2"></i>
                                            Este item já foi completamente estornado. 
                                            <strong> Quantidade estornada: {calcularTotalEstornado()} de {item.qtd}</strong>
                                        </div>
                                    ) : (
                                        <form onSubmit={realizarEstorno}>
                                            <div className="row">
                                                <div className="col-md-4 col-12 mt-md-0 mt-3">
                                                    <label className="form-label fw-bold">Quantidade a Estornar</label>
                                                    <input
                                                        type="number"
                                                        name="quantidade_estorno"
                                                        className="form-control"
                                                        value={formData.quantidade_estorno}
                                                        onChange={handleChange}
                                                        min="1"
                                                        max={item.qtd - calcularTotalEstornado()}
                                                        required
                                                    />
                                                    <small className="text-muted">
                                                        Máximo disponível: {item.qtd - calcularTotalEstornado()}
                                                    </small>
                                                </div>
                                                <div className="col-md-4 col-12 mt-md-0 mt-3">
                                                    <label className="form-label fw-bold">Método de Pagamento</label>
                                                    <select
                                                        name="tipo_pagamento"
                                                        className="form-select"
                                                        value={formData.tipo_pagamento}
                                                        onChange={handleChange}
                                                        required
                                                    >
                                                        <option value="">Selecione...</option>
                                                        <option value="pagamento_dinheiro">Dinheiro</option>
                                                        <option value="pagamento_credito">Cartão Crédito</option>
                                                        <option value="pagamento_debito">Cartão Débito</option>
                                                        <option value="pagamento_pix">Pix</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-4 col-12 mt-md-0 mt-3">
                                                    <label className="form-label fw-bold">Motivo do Estorno</label>
                                                    <input
                                                        type="text"
                                                        name="motivo"
                                                        className="form-control"
                                                        value={formData.motivo}
                                                        onChange={handleChange}
                                                        autoComplete="off"
                                                        placeholder="Descreva o motivo do estorno"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="row mt-3">
                                                <div className="col-12 text-end">
                                                    <button type="submit" className="btn btn-custom-primary">
                                                        <FaRegSave className="me-2" />
                                                        Realizar Estorno
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button className="btn btn-outline-danger" type="button" onClick={onClose}>
                            <FaTimes /> Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EstornosItemModal; 